import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Package, FileText, LayoutDashboard, Map, Sparkles, PieChart, X } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import powerToolsService from '../services/powerToolsService';

const QUICK_NAV = [
  { id: 'nav-dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { id: 'nav-customers', label: 'Customers', path: '/customers', icon: Users },
  { id: 'nav-units', label: 'Units', path: '/units', icon: Package },
  { id: 'nav-rentals', label: 'Rentals', path: '/rentals', icon: FileText },
  { id: 'nav-site-map', label: 'Site Map', path: '/site-map', icon: Map },
  { id: 'nav-portfolio', label: 'Portfolio', path: '/portfolio', icon: PieChart },
  { id: 'nav-assistant', label: 'Assistant', path: '/assistant', icon: Sparkles },
];

const CommandPalette = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ customers: [], units: [], rentals: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const runSearch = useCallback(
    async (q) => {
      if (!facilityId || !q || q.length < 2) {
        setResults({ customers: [], units: [], rentals: [] });
        return;
      }
      setLoading(true);
      try {
        const res = await powerToolsService.search(facilityId, q);
        setResults(res.data || { customers: [], units: [], rentals: [] });
      } catch (err) {
        console.error('Command palette search failed:', err);
      } finally {
        setLoading(false);
      }
    },
    [facilityId]
  );

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };

  const filteredNav = query
    ? QUICK_NAV.filter((n) => n.label.toLowerCase().includes(query.toLowerCase()))
    : QUICK_NAV;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 py-3 border-b border-slate-100">
          <Search className="h-4 w-4 text-slate-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customers, units, rentals, or jump to a page..."
            className="flex-1 text-[14px] text-slate-700 placeholder-slate-400 focus:outline-none"
          />
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {filteredNav.length > 0 && (
            <Section label="Navigate">
              {filteredNav.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNav(item.path)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                  >
                    <Icon className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[13px] text-slate-700">{item.label}</span>
                  </button>
                );
              })}
            </Section>
          )}

          {results.customers.length > 0 && (
            <Section label="Customers">
              {results.customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleNav(`/customers/${c.id}/edit`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                >
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-700 truncate">{c.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{c.phone || c.email}</p>
                  </div>
                </button>
              ))}
            </Section>
          )}

          {results.units.length > 0 && (
            <Section label="Units">
              {results.units.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleNav(`/units/${u.id}/edit`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                >
                  <Package className="h-3.5 w-3.5 text-slate-400" />
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-[13px] text-slate-700">{u.unit_number}</span>
                    <span className="text-[11px] text-slate-400">{u.unit_type}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 uppercase">{u.status}</span>
                </button>
              ))}
            </Section>
          )}

          {results.rentals.length > 0 && (
            <Section label="Rentals">
              {results.rentals.map((r) => (
                <button
                  key={r.id}
                  onClick={() => handleNav(`/rentals`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                >
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-700">Rental {r.id.slice(-6)}</p>
                  </div>
                  <span className="text-[11px] text-slate-500">${r.balance}</span>
                </button>
              ))}
            </Section>
          )}

          {!loading &&
            query.length >= 2 &&
            results.customers.length === 0 &&
            results.units.length === 0 &&
            results.rentals.length === 0 &&
            filteredNav.length === 0 && (
              <p className="px-4 py-6 text-center text-[13px] text-slate-400">No matches.</p>
            )}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-3 text-[10px] text-slate-400">
          <span>
            <kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">↵</kbd> select
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">esc</kbd> close
          </span>
          <span className="ml-auto">
            <kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">⌘K</kbd> to toggle
          </span>
        </div>
      </div>
    </div>
  );
};

const Section = ({ label, children }) => (
  <div>
    <p className="px-4 pt-3 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
      {label}
    </p>
    {children}
  </div>
);

export default CommandPalette;
